import {
  AssignmentInd,
  AttachFile,
  CloseOutlined,
  Download,
  Edit,
  ExpandLess,
  ExpandMore,
  Inbox,
  MarkChatRead,
  MarkEmailReadOutlined,
  PreviewOutlined,
  Reply,
  ReplyAll,
  Search,
  Send,
  Visibility,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  ClickAwayListener,
  Collapse,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Pagination,
  Paper,
  Popper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import {
  selectTokenValues,
  useAuthSlice,
} from "../../../redux/slice/authSlice";
import {
  setSingleAlertObj,
  showHideLoadingDialog,
  SingleAlertInfo,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import {
  acknowledgeAllNotifications,
  acknowledgeNotification,
  addNewMessage,
  composeNotification,
  fetchInboxMessageList,
  fetchSentboxMessageList,
  notificationAttachmentDTO,
  removeNotificationDocuments,
  resetNotificationData,
  uploadNotificationDocument,
  useCreateNotificationSlice,
} from "../../../redux/slice/inbox/createNotificationSlice";
import { fetchOrderDocumentsByIds } from "../../../redux/slice/order/OrderSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { RootState } from "../../../redux/store";
import Button from "../../common/Button";
import Card from "../../common/Card";
import CustomIconButton from "../../common/CustomIconButton";
import CustomTooltip from "../../common/CustomTooltip";
import OptionsPopup, {
  OptionPopupObjectProps,
} from "../../common/OptionsPopup";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useModalStyles } from "../../common/useModalStyles";
import ComposePopup from "./ComposePopup";
import InboxItem from "./InboxItem";
import { fetchDocumentById } from "../../../redux/slice/practiceSlice";
import { downloadBase64File } from "../../../utils/Utility";
import { downloadDocumentById } from "../../../redux/slice/manufacturerNotifications/ManufacturerNotificationSlice";
import AssignPopup from "./AssignPopup";
import { useLocation } from "react-router-dom";
import { useNavigation } from "../../../hooks/useNavigation";
import ReplyBox from "./ReplyBox";
import MessageMetaChip from "./MessageMetaChip";
import {
  fetchUsersByUserInput,
  useUserReducer,
} from "../../../redux/slice/userSlice";
import { RecipientOption } from "./CcBccField";

export interface Attachment {
  tempId: string; 
  documentName: string;
  content: string;
  fileType: string;
  type?: string
}

export interface InboxData {
  notification: any;
  isMulti: boolean;
  time: string;
  date: string;
  formattedDate: string;
  formattedTime: string;
  acknowledged: boolean;
  hasEntity: boolean;
  isAssignable: boolean;
  targetEntity?: string;
  targetEntityId?: number;
  displayName?: string;
  displayMsg?: string;
  displayDoc?: string;
}

const tabMap = {
  0: "unread",
  1: "read",
  2: "sent",
} as { [key: number]: string };

const InboxList: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { navigate } = useNavigation();
  const { primaryBtnSx, tokens: mTokens, isDark } = useModalStyles();

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";
  const listSelectedBg = isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.1)";

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.2)" : "0 6px 12px rgba(15,23,42,0.08)",
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const exportBtnSx = (color: string = "#14B8A6") => ({
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color,
    borderColor: `${color}8c`,
    backgroundColor: isDark ? `${color}14` : `${color}0a`,
    whiteSpace: "nowrap" as const,
    transition: "color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
    "&:hover": {
      color: "#fff",
      borderColor: color,
      backgroundColor: color,
      boxShadow: `0 6px 22px ${color}48`,
      transform: "translateY(-1px)",
      "& .MuiTypography-root": { color: "#fff" },
      "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
    },
  });

  // Mirrors CustomToolbar's per-action tint system — one color per tab/action instead of a
  // single uniform accent, so the sidebar and action rows are scannable at a glance.
  const navColors = {
    unread: "#f59e0b",
    read: "#14b8a6",
    sent: "#3b82f6",
  };

  const actionColors = {
    close: "#ef4444",
    assign: "#6366f1",
    reply: "#3b82f6",
    replyAll: "#8b5cf6",
    attachment: "#14b8a6",
    preview: "#3b82f6",
    download: "#6366f1",
  };

  // Flat nav row — icon + label + count, no card/border chrome, matching a plain folder-list sidebar.
  const sidebarNavSelectedSx = (color: string) => ({
    px: 1.25,
    py: 0.9,
    mb: 0.25,
    borderRadius: "8px",
    color: mTokens.textSecondary,
    transition: "color 0.15s ease, background-color 0.15s ease",
    "& .MuiListItemIcon-root": { color: "inherit", transition: "color 0.15s ease" },
    "&.Mui-selected": {
      backgroundColor: "transparent",
      color: mTokens.textPrimary,
      "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)" },
      "& .MuiListItemIcon-root": { color },
      "& .MuiTypography-root": {
        color: mTokens.textPrimary,
        fontWeight: 700,
      },
    },
    "&:hover": {
      backgroundColor: isDark ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.03)",
      color: mTokens.textPrimary,
    },
  });

  const composeBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.9rem",
    borderRadius: "999px",
    py: 1.1,
    color: "#0f172a",
    backgroundColor: "#ffffff",
    boxShadow: isDark ? "0 4px 14px rgba(0,0,0,0.35)" : "0 2px 10px rgba(15,23,42,0.12)",
    "& .MuiTypography-root": { color: "#0f172a", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#0f172a" },
    "&:hover": {
      backgroundColor: "#f1f5f9",
      boxShadow: isDark ? "0 4px 14px rgba(0,0,0,0.4)" : "0 2px 10px rgba(15,23,42,0.16)",
    },
  };

  const searchBoxSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 1.5,
    py: 1,
    borderRadius: "10px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)",
  };

  const closeIconBtnSx = {
    width: 36,
    height: 36,
    borderRadius: "10px",
    border: `1px solid ${mTokens.border}`,
    color: mTokens.textSecondary,
    backgroundColor: mTokens.bg,
    transition: "color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      color: actionColors.close,
      borderColor: actionColors.close,
      backgroundColor: isDark ? `${actionColors.close}1f` : `${actionColors.close}14`,
      transform: "translateY(-2px)",
      boxShadow: `0 6px 14px ${actionColors.close}30`,
    },
    "&:active": { transform: "translateY(0) scale(0.94)" },
  };

  // Tinted circular hover for inline attachment/thread icon buttons — same lift/glow language as the toolbar.
  const tintedIconBtnSx = (color: string) => ({
    borderRadius: "8px",
    color: mTokens.textSecondary,
    transition: "color 0.18s ease, background-color 0.18s ease, transform 0.18s ease",
    "&:hover": {
      color,
      backgroundColor: isDark ? `${color}20` : `${color}14`,
      transform: "translateY(-1px)",
    },
  });

  // Identical "pill" surface CustomToolbar wraps its action-icon cluster in — reused here so
  // grouped controls (header actions, sidebar nav, attachment icons) read as the same system.
  const pillGroupSx = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: 0.35,
    p: 0.4,
    borderRadius: "14px",
    backgroundColor: isDark ? "rgba(255,255,255,0.025)" : "rgba(15,23,42,0.025)",
    border: `1px solid ${mTokens.border}`,
  };

  const pillDividerSx = {
    borderColor: mTokens.border,
    my: 0.75,
    mx: 0.35,
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [showPopup, setShowPopup] = React.useState(false);
  const [composeMode, setComposeMode] = React.useState<
    "group" | "user" | "reply" | null
  >(null);
  const notifications = useSelector(
    (state: RootState) => state.createNotificationSlice.notifications
  );
  const { userInfo } = useAuthSlice();
  const dispatch = useAppDispatch();
  const { InboxListInfo } = useCreateNotificationSlice();
  const [inboxData, setInboxData] = React.useState<InboxData[]>([]);
  const [searchQuery, setSearchQuery] = React.useState("");

  const initialActiveTab = location.state?.notificationType ?? "unread";
  const initialTab =
    Number(
      Object.keys(tabMap).find(
        (key) => tabMap[Number(key)] === initialActiveTab
      )
    ) ?? 0;
  const [tab, setTab] = React.useState(initialTab);
  const [activeTab, setActiveTab] = React.useState<string>(initialActiveTab);
  const [totalPages, setToatalPages] = React.useState<number>(1);
  const [totalElements, setTotalElements] = React.useState<number>(1);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [showOptionPopup, setShowOptionsPopup] =
    React.useState<OptionPopupObjectProps>({ message: "", show: false });
  const accessibleNotifications = useSelector((state: RootState) =>
    selectTokenValues("canAccessNotifications")(state)
  );
  const accessibleTypes = useSelector((state: RootState) =>
    selectTokenValues("canAccessInbox")(state)
  );
  const [selectedMessage, setSelectedMessage] =
    React.useState<InboxData | null>(null);
  const [showPDF, setShowPDF] = React.useState<{
    isShow: boolean;
    title: string;
    fileName: string;
  }>({ isShow: false, title: "", fileName: "" });
  const [pdfContent, setPDFContent] = React.useState<string>("");
  const { documentDataFromCommonApi } = useCommonReducer();
  const [showAttachments, setShowAttachments] = useState(false);
  const attachments =
    selectedMessage?.notification.notificationAttachment || [];
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAssignPopup, setShowAssignPopup] =
    React.useState<boolean>(false);
  const uploadedReplyBoxAttachmentsRef = useRef<{ tempId: string; id: number }[]>([]);
  const uploadedComposeAttachmentsRef = useRef<{ tempId: string; id: number }[]>([]);
  const [uploadedReplyBoxAttachments, setUploadedReplyBoxAttachments] = useState<{ tempId: string; id: number }[]>([]);
  const [uploadedComposeAttachments, setUploadedComposeAttachments] = useState<{ tempId: string; id: number }[]>([]);
 
  const { fetchUsersByQuery } = useUserReducer();

  const replyBoxUserOptions: RecipientOption[] = React.useMemo(() => {
    if (!fetchUsersByQuery) return [];
    return fetchUsersByQuery.map((item: any) => ({
      value: parseInt(item.userId),
      label: `${item.firstName} ${
        item.middleName ? item.middleName + " " : ""
      }${item.lastName} (${item.email})`,
    }));
  }, [fetchUsersByQuery]); 

  const open = Boolean(anchorEl);

  const ITEMS_PER_PAGE = 10;

  const menuItems = [
    { label: "Unread", icon: <Inbox />, color: navColors.unread },
    { label: "Read", icon: <MarkEmailReadOutlined />, color: navColors.read },
    { label: "Sent", icon: <Send />, color: navColors.sent },
  ];

  // Fetch user list for Cc/Bcc on mount
  useEffect(() => {
    dispatch(fetchUsersByUserInput({ searchQuery: "", skipLoader: true }));
  }, []);

  useEffect(() => {
    if (!notifications || notifications[0].senderId == null) return;
    if (notifications.length > 0) {
      if (
        !notifications[0].notificationAckId &&
        notifications[0].senderId != null &&
        accessibleNotifications.includes(
          notifications[0].notificationType ?? ""
        )
      ) {
        const isSentByLoggedUser =
          notifications[0].senderId === userInfo.userId;
        const hasNoChildNotifications =
          !notifications[0].childNotifications ||
          notifications[0].childNotifications.length === 0;

        const shouldSkip = isSentByLoggedUser && hasNoChildNotifications;
        if (shouldSkip) return;

        const newTotal = totalElements + 1;
        setTotalElements(newTotal);

        const incomingParentId =
          notifications[0]?.parentNotification?.notificationId;

        if (activeTab === "unread") {
          if (
            selectedMessage &&
            threadMessages.length > 0 &&
            incomingParentId
          ) {
            const threadIndexNew = threadMessages.findIndex(
              (thread: any) =>
                thread?.parentNotification?.notificationId ===
                  incomingParentId ||
                thread?.notificationId === incomingParentId
            );
            if (threadIndexNew !== -1) {
              const formatted = formatAuditTimestamp(
                notifications[0]?.auditTimeStamp ?? null
              );
              const updatedMessage = {
                ...notifications[0],
                formattedDate: formatted.date,
                formattedTime: formatted.time,
              };
              threadMessages.unshift(updatedMessage);

              const matchedIndex = inboxData.findIndex(
                (msg: any) =>
                  msg.notification.notificationId === incomingParentId ||
                  msg.notification.parentNotification?.notificationId ===
                    incomingParentId
              );

              if (matchedIndex !== -1) {
                const updatedInbox = [...inboxData];
                updatedInbox[matchedIndex] = {
                  ...updatedInbox[matchedIndex],
                  notification: updatedMessage,
                };
                setInboxData(updatedInbox);
              }
            }
          } else if (selectedMessage === null) {
            const incoming = notifications[0];
            const formatted = formatAuditTimestamp(
              incoming?.auditTimeStamp ?? null
            );
            const updatedNotification = {
              ...incoming,
              formattedDate: formatted.date,
              formattedTime: formatted.time,
            };

            const matchedIndex = inboxData.findIndex(
              (msg: any) =>
                incomingParentId &&
                incomingParentId != null &&
                (msg.notification.notificationId === incomingParentId ||
                  msg.notification.parentNotification?.notificationId ===
                    incomingParentId)
            );

            if (matchedIndex !== -1) {
              const updatedInbox = [...inboxData];
              updatedInbox[matchedIndex] = {
                ...updatedInbox[matchedIndex],
                notification: updatedNotification,
              };
              setInboxData(updatedInbox);
            } else {
              dispatch(addNewMessage(notifications[0]));
            }
          } else {
            dispatch(addNewMessage(notifications[0]));
          }
        }

        const threadIndex = inboxData.findIndex(
          (thread: any) =>
            thread?.notification?.parentNotification?.notificationId ===
            incomingParentId
        );

        if (threadIndex !== -1) {
          if (activeTab === "read") {
            setInboxData((prev) =>
              prev.filter((_, idx) => idx !== threadIndex)
            );
            setSelectedMessage(null);
          } else if (activeTab === "sent") {
            if (
              selectedMessage &&
              threadMessages.length > 0 &&
              incomingParentId
            ) {
              const threadIndexNew = threadMessages.findIndex(
                (thread: any) =>
                  thread?.parentNotification?.notificationId ===
                  incomingParentId
              );
              if (threadIndexNew !== -1) {
                const formatted = formatAuditTimestamp(
                  notifications[0]?.auditTimeStamp ?? null
                );
                const updatedMessage = {
                  ...notifications[0],
                  formattedDate: formatted.date,
                  formattedTime: formatted.time,
                };
                threadMessages.unshift(updatedMessage);
              }
            }
          }
        }
      } else {
        const isAckOnceByOtherUser =
          notifications[0].ackUsrId !== userInfo.userId &&
          notifications[0].ackType === "ACK_ONCE";
        if (
          typeof notifications[0].ackUsrId !== "undefined" &&
          isAckOnceByOtherUser
        ) {
          refreshPage();
          let alertMessage = "";
          if (notifications[0].ackedAll) {
            alertMessage =
              "All group messages have been marked as read by another user";
          } else {
            alertMessage =
              "A group message has been marked as read by another user";
          }
          const alertObj: SingleAlertInfo = {
            message: alertMessage,
            alertType: "success",
          };
          dispatch(setSingleAlertObj(alertObj));
        }
      }
    }
  }, [notifications]);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  useEffect(() => {
    setCurrentPage(1);
    setActiveTab(tabMap[tab] || "");
    setSelectedMessage(null);
  }, [tab]);

  useEffect(() => {
    if (["unread", "read", "all"].includes(activeTab)) {
      fetchInitialNotificationList();
    } else if (activeTab === "sent") {
      fetchInitialSentBox();
    }
  }, [activeTab, currentPage]);

  const refreshPage = () => {
    setSelectedMessage(null);
    if (activeTab === "unread" || activeTab === "read") {
      fetchInitialNotificationList();
    } else if (activeTab === "sent") {
      fetchInitialSentBox();
    }
  };

  const fetchInitialNotificationList = async () => {
    dispatch(showHideLoadingDialog(true));
    await dispatch(
      fetchInboxMessageList({
        notificationTypes: accessibleNotifications
          ? accessibleNotifications
          : [],
        unread: activeTab === "unread" ? true : false,
        notificationCategory: "user",
        currentPage: currentPage,
      })
    );
    dispatch(showHideLoadingDialog(false));
  };

  const fetchInitialSentBox = async () => {
    dispatch(showHideLoadingDialog(true));
    await dispatch(
      fetchSentboxMessageList({
        notificationTypes: [
          "TO_SUPER_ADMIN",
          "TO_ADMIN",
          "TO_USER",
          "BROADCAST",
          "TO_MANU",
          "TO_PRACTICE",
          "TO_AGENCY",
        ],
        currentPage: currentPage,
      })
    );
    dispatch(showHideLoadingDialog(false));
  };

  useEffect(() => {
    if (location?.state?.notificationId && inboxData.length > 0) {
      const targetId = Number(location.state.notificationId);
      const found = inboxData.find(
        (item) => item.notification.notificationId === targetId
      );
      if (found) {
        setSelectedMessage(found);
        if (location?.state?.forReply) {
          openReplyFor(found, "reply");
        }
      }
      navigate(location.pathname, { replace: true });
    }
  }, [
    location?.state?.notificationId,
    inboxData,
    location?.state?.forReply,
  ]);

  const formatAuditTimestamp = (timestamp: string | null) => {
    if (!timestamp) return { date: "", time: "" };
    const localTime = dayjs.utc(timestamp).local();
    return {
      date: localTime.format("MM/DD/YYYY"),
      time: localTime.format("hh:mm A"),
    };
  };

  const checkAssignability = (message: any) => {
    if (
      (message.notificationType === "TO_USER" ||
        (message.notificationType === "TO_ADMIN" &&
          message.targetEntityId != 0)) &&
      activeTab === "unread" &&
      message.senderId != userInfo.userId
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (
      !InboxListInfo ||
      (InboxListInfo.content[0] && InboxListInfo.content[0].senderId == null)
    )
      return;

    setToatalPages(InboxListInfo.totalPages);
    setTotalElements(InboxListInfo.totalElements);

    const mappedData: InboxData[] = InboxListInfo.content.map((item: any) => {
      let date = "";
      let time = "";
      let formattedDate = "";
      let formattedTime = "";

      const assignedToEntry = item.notificationAsgnTo?.find(
        (assign: any) => assign.assignedToUserId === userInfo.userId
      );
      if (assignedToEntry) {
        const [splitDate, splitTime] =
          assignedToEntry.assignedToAuditTimeStamp.split(" ");
        const formatted = formatAuditTimestamp(
          assignedToEntry.assignedToAuditTimeStamp
        );
        date = splitDate || "";
        time =
          splitTime +
          " " +
          assignedToEntry.assignedToAuditTimeStamp.split(" ")[2];
        formattedDate = formatted.date;
        formattedTime = formatted.time;
      } else {
        const [splitDate, splitTime] = item.auditTimeStamp.split(" ");
        const formatted = formatAuditTimestamp(item.auditTimeStamp);
        date = splitDate || "";
        time = splitTime + " " + item.auditTimeStamp.split(" ")[2];
        formattedDate = formatted.date;
        formattedTime = formatted.time;
      }

      return {
        notification: item,
        isMulti:
          item.parentNotification != null ||
          (item.childNotifications && item.childNotifications.length > 0)
            ? true
            : false,
        date,
        time,
        formattedDate,
        formattedTime,
        acknowledged: item.acknowledged || false,
        hasEntity: item.entity !== null && item.entityId !== null,
        isAssignable: checkAssignability(item),
        targetEntity: item.targetEntity,
        targetEntityId: item.targetEntityId,
      };
    });

    setInboxData(mappedData);
  }, [InboxListInfo]);

  const getLabel = (dateStr: string) => {
    const date = dayjs(dateStr);
    const today = dayjs();
    const yesterday = today.subtract(1, "day");

    if (date.isSame(today, "day")) return "Today";
    if (date.isSame(yesterday, "day")) return "Yesterday";
    if (date.isAfter(today.subtract(7, "day"))) return "Last 7 days";
    if (date.month() === today.month() && date.year() === today.year())
      return "Earlier this month";

    return date.format("MMMM YYYY");
  };

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const idParam = urlSearchParams.get("id");

    if (!idParam || !inboxData) return;

    const id = parseInt(idParam, 10);

    const containsNotificationId = (
      notification: any,
      targetId: number
    ): boolean => {
      if (!notification) return false;
      if (notification.notificationId === targetId) return true;
      if (
        notification.parentNotification &&
        containsNotificationId(
          notification.parentNotification,
          targetId
        )
      )
        return true;
      if (Array.isArray(notification.childNotifications)) {
        for (const child of notification.childNotifications) {
          if (containsNotificationId(child, targetId)) return true;
        }
      }
      if (
        notification.parentNotification &&
        Array.isArray(
          notification.parentNotification.childNotifications
        )
      ) {
        for (const child of notification.parentNotification
          .childNotifications) {
          if (containsNotificationId(child, targetId)) return true;
        }
      }
      return false;
    };

    const matchedNotification = inboxData.find((msg: InboxData) =>
      containsNotificationId(msg.notification, id)
    );

    if (matchedNotification) {
      setSelectedMessage(matchedNotification);
    } else {
      setSelectedMessage(null);
    }
  }, [inboxData]);

  const searchedInboxData = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return inboxData;
    return inboxData.filter((item) => {
      const n = item.notification;
      const haystack = [
        n.senderName,
        n.targetName,
        n.notificationSub,
        n.parentNotification?.notificationSub,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [inboxData, searchQuery]);

  const grouped = searchedInboxData?.reduce((acc, item) => {
    const label = getLabel(item.formattedDate);
    if (!acc[label]) acc[label] = [];
    acc[label].push(item);
    return acc;
  }, {} as Record<string, InboxData[]>);

  Object.keys(grouped).forEach((label) => {
    grouped[label].sort((a, b) => {
      const aDateTime = dayjs(`${a.formattedDate} ${a.formattedTime}`);
      const bDateTime = dayjs(`${b.formattedDate} ${b.formattedTime}`);
      return bDateTime.valueOf() - aDateTime.valueOf();
    });
  });

  const sortPriority = [
    "Today",
    "Yesterday",
    "Last 7 days",
    "Earlier this month",
  ];

  const sortedGroups = Object.keys(grouped).sort((a, b) => {
    const aIndex = sortPriority.indexOf(a);
    const bIndex = sortPriority.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    else if (aIndex !== -1) return -1;
    else if (bIndex !== -1) return 1;
    else
      return (
        dayjs(b).toDate().getTime() - dayjs(a).toDate().getTime()
      );
  });

  const isEmpty = inboxData.length === 0;

  const saveReplyBoxNotificationDocs = async (attachments: Attachment[]) => {
    dispatch(showHideLoadingDialog(true));
    for (const attachment of attachments) {
      const response = await dispatch(uploadNotificationDocument(attachment)).unwrap();

      if (response?.data?.documentId) {
        setUploadedReplyBoxAttachments((prev) => [
          ...prev,
          { tempId: attachment.tempId, id: response.data.documentId },
        ]);
      }
    }
    dispatch(showHideLoadingDialog(false));
  };

    const saveComposePopupNotificationDocs = async (attachments: Attachment[]) => {
    dispatch(showHideLoadingDialog(true));
    for (const attachment of attachments) {
      const response = await dispatch(uploadNotificationDocument(attachment)).unwrap();

      if (response?.data?.documentId) {
        setUploadedComposeAttachments((prev) => [
          ...prev,
          { tempId: attachment.tempId, id: response.data.documentId },
        ]);
      }
    }
    dispatch(showHideLoadingDialog(false));
  };

  const handleReplyBoxRemoveAttachment = (attachment: Attachment) => {
    const found = uploadedReplyBoxAttachments.find(
      (a) => a.tempId === attachment.tempId
    );

    if (found) {
      dispatch(removeNotificationDocuments([found.id]));

      setUploadedReplyBoxAttachments((prev) =>
        prev.filter((a) => a.id !== found.id)
      );
    }
  };

  const returnCCIds = (msg: any) => {
    const ccIds = msg?.ccIds ?? msg?.notification?.ccIds ?? [];
    const fromattedccIds = ccIds.filter((id: number) => id != userInfo.userId);
    const bccIds = msg?.bccIds ?? msg?.notification?.bccIds ?? [];
    if(bccIds.includes(userInfo.userId) || ccIds.includes(userInfo.userId)) {
      fromattedccIds.push(msg?.targetEntityId ?? msg?.notification?.targetEntityId);
    }
    return fromattedccIds;
  }

   const handleComposePopupRemoveAttachment = (attachment: Attachment) => {
    const found = uploadedComposeAttachments.find(
      (a) => a.tempId === attachment.tempId
    );

    if (found) {
      dispatch(removeNotificationDocuments([found.id]));

      setUploadedComposeAttachments((prev) =>
        prev.filter((a) => a.id !== found.id)
      );
    }
  };

  const sendingReplyBoxFunction = async (
    toValue: string,
    entity: number[],
    subject: string,
    message: string,
    important: boolean,
    attachments: Attachment[],
    parentId?: number,
    repliedMsg?: any,
    cc?: number[],
    bcc?: number[]
  ) => {
    const attachmentIds = uploadedReplyBoxAttachments.map((a) => a.id);
    try {
      const composeData = {
        targetEntity: toValue ? toValue : "",
        targetEntityId: entity,
        subject: subject,
        notificationMsg: message,
        important: important,
        attachmentIds: attachmentIds,
        parentId: parentId,
        cc: cc ?? [],
        bcc: bcc ?? [],
      };
      const response = await dispatch(
        composeNotification(composeData)
      ).unwrap();
      if (response) {
        if (
          parentId != null &&
          selectedMessage != null &&
          activeTab != "read"
        ) {
          if (
            selectedMessage.notification.notificationType != "USER" ||
            (selectedMessage.notification.parentNotification != null &&
              selectedMessage.notification.parentNotification
                .notificationType != "USER")
          ) {
            await handleAck(repliedMsg);
          } else {
            await handleAck(selectedMessage.notification);
          }
        }
        setUploadedReplyBoxAttachments([]);
        refreshPage();
      }
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  }; 

    const sendingComposePopupFunction = async (
    toValue: string,
    entity: number[],
    subject: string,
    message: string,
    important: boolean,
    attachments: Attachment[],
    parentId?: number,
    repliedMsg?: any,
    cc?: number[],
    bcc?: number[]
  ) => {
    const attachmentIds = uploadedComposeAttachments.map((a) => a.id);
    try {
      const composeData = {
        targetEntity: toValue ? toValue : "",
        targetEntityId: entity,
        subject: subject,
        notificationMsg: message,
        important: important,
        attachmentIds: attachmentIds,
        parentId: parentId,
        cc: cc ?? [],
        bcc: bcc ?? [],
      };
      const response = await dispatch(
        composeNotification(composeData)
      ).unwrap();
      if (response) {
        if (
          parentId != null &&
          selectedMessage != null &&
          activeTab != "read"
        ) {
          if (
            selectedMessage.notification.notificationType != "USER" ||
            (selectedMessage.notification.parentNotification != null &&
              selectedMessage.notification.parentNotification
                .notificationType != "USER")
          ) {
            await handleAck(repliedMsg);
          } else {
            await handleAck(selectedMessage.notification);
          }
        }
        setUploadedComposeAttachments([]);
        refreshPage();
      }
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  }; 


  const handleAckAllAction = async () => {
    setShowOptionsPopup({
      message:
        "Are you sure you want to mark all unread group messages as read? This action will also mark these messages as read for other users in your group as well.",
      show: true,
      variant: "confirm",
      buttons: [
        {
          name: "Confirm",
          color: "primary",
          onClick: () => ackAllNotifications(),
        },
        { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
      ],
    });
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const ackAllNotifications = async () => {
    cancelOptionPopup();
    try {
      const reqData = {
        notificationTypes: accessibleNotifications
          ? accessibleNotifications
          : [],
        unread: true,
        notificationCategory: "user",
      };
      const response = await dispatch(
        acknowledgeAllNotifications(reqData)
      ).unwrap();
      if (response.message === "SUCCESS") {
        refreshPage();
      }
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  };

  useEffect(
    () => () => {
      removeComposePopupAttachments();
      closeReply();
      dispatch(resetNotificationData());
    },
    []
  );

  const stripHtml = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleActionPreview = async (
    attachment: notificationAttachmentDTO,
    type: string
  ) => {
    const documentId = attachment.documentId;
    if (documentId) {
      const actionResult = await dispatch(
        fetchOrderDocumentsByIds([documentId])
      );
      const documentData = actionResult.payload;

      if (documentData?.content) {
        setPDFContent(documentData.content);
        setShowPDF({
          isShow: true,
          title: documentData.patientName,
          fileName: documentData.documentName,
        });
      } else {
        dispatch(
          setSingleAlertObj({
            message:
              "Requested Document has not found under server location!",
            alertType: "info",
          })
        );
      }
    }
  };

  const handleAck = async (message: any) => {
    try {
      const notificationData = {
        notificationId: message.notificationId,
        comment: "",
      };
      const response = await dispatch(
        acknowledgeNotification(notificationData)
      ).unwrap();
      refreshPage();
      return response;
    } catch (err) {
      console.error("Failed to acknowledge message:", err);
      return [];
    }
  };

  const handleDownloadAll = async (documentIds: number[]) => {
    for (const documentId of documentIds) {
      if (documentId) {
        const actionResult = await dispatch(
          fetchDocumentById(documentId.toString())
        );
        const documentData = actionResult.payload;
        if (documentData?.content) {
          downloadBase64File(
            documentData.content,
            documentData.documentName,
            documentData.fileType
          );
        } else {
          dispatch(
            setSingleAlertObj({
              message: "Document not found!",
              alertType: "info",
            })
          );
        }
      }
    }
  };

  const handleAssign = async () => {
    setShowAssignPopup(true);
  };

  const [replyForId, setReplyForId] = useState<number | null>(null);
  const [replyMode, setReplyMode] = useState<string>();

  const openReplyFor = (msg: any, mode: string) => {
    setReplyMode(mode);
    if (msg.notificationId) {
      setReplyForId(msg.notificationId);
    } else if (msg.notification.notificationId) {
      setReplyForId(msg.notification.notificationId);
    }
    setTimeout(() => {
      const el = document.getElementById("reply-bar-anchor");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  const closeReply = () => {
    setReplyForId(null);
    const attachmentIds = uploadedReplyBoxAttachmentsRef.current?.map((a) => a.id);
    if (attachmentIds?.length > 0) {
      dispatch(removeNotificationDocuments(attachmentIds));
    }
    setUploadedReplyBoxAttachments([]);
  };

  const closeComposePopup = () => {
    setShowPopup(false);
    setComposeMode(null);
  };

  useEffect(() => {
    uploadedReplyBoxAttachmentsRef.current = uploadedReplyBoxAttachments;
  }, [uploadedReplyBoxAttachments]);

  useEffect(() => {
    uploadedComposeAttachmentsRef.current = uploadedComposeAttachments;
  },[uploadedComposeAttachments])

  const removeComposePopupAttachments = () => {
    const attachmentIds = uploadedComposeAttachmentsRef.current?.map((a) => a.id);
    if (attachmentIds?.length > 0) {
      dispatch(removeNotificationDocuments(attachmentIds));
    }
    setUploadedComposeAttachments([]);
  }

  useEffect(() => {
    if (!selectedMessage) {
      setReplyForId(null);
      return;
    }
  }, [selectedMessage?.notification?.notificationId, activeTab]);

  const [threadMessages, setThreadMessages] = useState<any[]>([]);

  useEffect(() => {
    if (!selectedMessage) {
      setThreadMessages([]);
      return;
    }

    const messages: any[] = [];
    const notification = selectedMessage.notification;

    if (notification.parentNotification) {
      const parent = notification.parentNotification;
      messages.push(parent);
      const siblings =
        parent.childNotifications?.filter(
          (sib: any) =>
            sib.notificationId !== notification.notificationId
        ) || [];
      messages.push(...siblings);
    }

    messages.push(notification);

    if (notification.childNotifications?.length) {
      messages.push(...notification.childNotifications);
    }

    messages.sort(
      (a, b) =>
        new Date(b.auditTimeStamp).getTime() -
        new Date(a.auditTimeStamp).getTime()
    );

    const updatedMessages = messages.map((msg) => {
      const formatted = formatAuditTimestamp(msg.auditTimeStamp);
      return {
        ...msg,
        formattedDate: formatted.date,
        formattedTime: formatted.time,
        isAssignable: checkAssignability(msg),
      };
    });

    setThreadMessages(updatedMessages);
  }, [selectedMessage]);

  useEffect(() => {
    if (!threadMessages || threadMessages.length === 0) return;
  }, [threadMessages]);

  const [collapsedMap, setCollapsedMap] = useState<Record<number, boolean>>(
    {}
  );
  const toggleCollapsed = (id: number) =>
    setCollapsedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  const isCollapsed = (id: number) => !!collapsedMap[id];

  const buildAvatarSrc = (
    doc?: { fileType?: string; content?: string } | null
  ) => (doc ? `data:image/jpeg;base64,${doc}` : undefined);

  const getListItemAvatarDoc = (
    item: any
  ): { doc: any | null; fallback: string } => {
    const notification = item.notification;
    if (item.isMulti) {
      if (notification.senderId === userInfo.userId) {
        const fallback = notification.targetName?.[0] || "U";
        return {
          doc:
            notification.pfpMap?.[notification.targetEntityId] || null,
          fallback,
        };
      } else {
        const fallback = notification.senderName?.[0] || "U";
        return {
          doc: notification.pfpMap?.[notification.senderId] || null,
          fallback,
        };
      }
    } else {
      if (activeTab === "sent") {
        const fallback = notification.targetName?.[0] || "U";
        return {
          doc:
            notification.pfpMap?.[notification.targetEntityId] || null,
          fallback,
        };
      } else {
        const fallback = notification.senderName?.[0] || "U";
        return {
          doc: notification.pfpMap?.[notification.senderId] || null,
          fallback,
        };
      }
    }
  };

  const getThreadMessageAvatarDoc = (
    mainMsg: any,
    msg: any,
    isMulti: boolean,
    activeTab: string
  ): { doc: any | null; fallback: string } => {
    if (activeTab === "sent") {
      if (isMulti) {
        const fallback = msg.senderName?.[0] || "U";
        return {
          doc:
            mainMsg.notification.pfpMap?.[msg.senderId] || null,
          fallback,
        };
      } else {
        const fallback = msg.targetName?.[0] || "U";
        return {
          doc:
            mainMsg.notification.pfpMap?.[msg.targetEntityId] || null,
          fallback,
        };
      }
    } else {
      const fallback = msg.senderName?.[0] || "U";
      return {
        doc: mainMsg.notification.pfpMap?.[msg.senderId] || null,
        fallback,
      };
    }
  };

  const getReplyHeaderAvatarDoc = (
    mainMsg: any,
    msg: any,
    activeTab: string
  ): { doc: any | null; fallback: string } => {
    if (!msg) return { doc: null, fallback: "U" };
    const n = msg;
    if (activeTab === "sent") {
      return {
        doc: n.pfpMap?.[n.targetEntityId] || null,
        fallback: n.targetName?.[0] || "U",
      };
    }
    if (n.notificationAsgnTo?.length > 0) {
      const assignedName =
        n.notificationAsgnTo[0].assignedToAuditUsername || "U";
      return {
        doc: n.pfpMap?.[n.targetEntityId] || null,
        fallback: assignedName[0] || "U",
      };
    }
    return {
      doc: mainMsg.notification.pfpMap?.[n.senderId] || null,
      fallback: n.senderName?.[0] || "U",
    };
  };

  const chooseTitleName = (item: any) => {
    const { notification, isMulti } = item;
    const parent = notification.parentNotification;
    const type = isMulti
      ? parent?.notificationType ?? notification.notificationType
      : notification.notificationType;

    const isSender = notification.senderId === userInfo.userId;
    const specialCase = [
      "TO_ADMIN",
      "TO_SUPER_ADMIN",
      "BROADCAST",
    ].includes(type);

    const getTargetOrParentName = () =>
      parent?.targetName ?? notification.targetName;

    if (activeTab === "sent") {
      if (specialCase) {
        if (parent != null && parent.senderId === userInfo.userId) {
          return getTargetOrParentName();
        } else {
          return isSender
            ? notification.targetName
            : notification.senderName;
        }
      }
      return isSender ? notification.targetName : notification.senderName;
    }

    return isSender ? notification.targetName : notification.senderName;
  };

  const isBelongtoThisUser = (msg: any) => {
    let parentType = null;
    let parentMsg = null;
    if (selectedMessage?.notification.parentNotification != null) {
      parentMsg = selectedMessage?.notification.parentNotification;
      parentType =
        selectedMessage?.notification.parentNotification.notificationType;
    } else if (
      selectedMessage?.isMulti &&
      selectedMessage.notification.childNotifications.length > 0
    ) {
      parentMsg = selectedMessage?.notification;
      parentType = selectedMessage?.notification.notificationType;
    }

    if (parentMsg != null && parentMsg.senderId === userInfo.userId) {
      return true;
    }
    if (selectedMessage?.isMulti && parentType != "TO_USER") {

  const notification = msg?.notification ?? {};
  const ccIds = msg?.ccIds ?? notification.ccIds ?? [];
  const bccIds = msg?.bccIds ?? notification.bccIds ?? [];

  const isBccUser = bccIds.includes(userInfo.userId);
  const isCcUser = ccIds.includes(userInfo.userId);

      if (
        msg.senderId === userInfo.userId ||
        msg.targetEntityId === userInfo.userId
      ) {
        return true;
      } else if (msg.notificationType != "TO_USER") {
        return true;
      } else if(isBccUser || isCcUser) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const userOptionIdSet = React.useMemo(() => {
    return new Set(replyBoxUserOptions.map((opt) => opt.value));
  }, [replyBoxUserOptions]);

  const areAllIdsPresent = (ids?: number[]) => {
    if (!ids || ids.length === 0) return true;
    return ids.every((id) => userOptionIdSet.has(id));
  };

const ccAndBccValidation = (msg: any) => {
  const notification = msg?.notification ?? {};
  const ccIds = msg?.ccIds ?? notification.ccIds ?? [];
  const fromattedccIds = ccIds.filter((id: number) => id != userInfo.userId);
  const bccIds = msg?.bccIds ?? notification.bccIds ?? [];
  const targetId = msg?.targetEntityId ?? notification.targetEntityId;

  const isBccUser = bccIds.includes(userInfo.userId);
  const isCcUser = ccIds.includes(userInfo.userId);

  const idsToCheck = [
    ...fromattedccIds,
    ...((isBccUser || isCcUser) && targetId ? [targetId] : [])
  ];

  return idsToCheck.length > 0 && areAllIdsPresent(idsToCheck);
};

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2,
        pb: { xs: 1, sm: 2 },
      }}
    >
      {showAssignPopup && selectedMessage && (
        <AssignPopup
          mode="assign"
          canAssign={selectedMessage.isAssignable}
          isAcknowledged={false}
          attachments={
            selectedMessage.notification.notificationAttachment
              ? selectedMessage.notification.notificationAttachment
              : []
          }
          open={showAssignPopup}
          onClose={() => setShowAssignPopup(false)}
          onSubmitAssign={() => {
            refreshPage();
            setSelectedMessage(null);
          }}
          notification={{
            notificationId: selectedMessage.notification.notificationId,
            subject: selectedMessage.notification.notificationSub
              ? selectedMessage.notification.notificationSub
              : "",
            message: stripHtml(
              selectedMessage.notification.notificationMsg
                ? selectedMessage.notification.notificationMsg
                : ""
            ),
            category:
              selectedMessage.notification.senderId != null
                ? "user"
                : "system",
            time: selectedMessage.notification.auditTimeStamp
              ? selectedMessage.notification.auditTimeStamp
              : "",
            senderId: selectedMessage.notification.senderId,
            notificationAcks:
              selectedMessage.notification.notificationAck,
          }}
        />
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        gap={2}
        flexWrap="wrap"
        data-testid="header"
      >
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: mTokens.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              mb: 0.3,
            }}
          >
            Messages
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              fontWeight: 800,
              color: mTokens.textPrimary,
              lineHeight: 1.1,
            }}
          >
            Inbox
          </Typography>
        </Box>
        <Box sx={pillGroupSx}>
          {activeTab === "unread" && !isEmpty && (
            <>
              <Button
                onClick={handleAckAllAction}
                variant="contained"
                startIcon={<MarkChatRead />}
                removeUpperCase
                sx={{ ...primaryActionBtnSx, mx: 0.5 }}
              >
                Mark All as Read
              </Button>
              <Divider orientation="vertical" flexItem sx={pillDividerSx} />
            </>
          )}
          <IconButton
            aria-label="close"
            onClick={() => navigate(-1)}
            sx={closeIconBtnSx}
          >
            <CloseOutlined />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          ...listCardSx,
          display: "flex",
          alignItems: "stretch",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",
          flex: 1,
          minHeight: { xs: "auto", md: 0 },
        }}
      >
          <Box
            sx={{
              width: { xs: "100%", md: 232 },
              flexShrink: 0,
              p: 1.5,
              overflowY: "auto",
            }}
          >
              <Box>
                <Button
                  onClick={handleToggle}
                  variant="contained"
                  startIcon={<Edit />}
                  fullWidth
                  removeUpperCase
                  sx={composeBtnSx}
                >
                  Compose
                </Button>
              </Box>

              <Box mt={2.5}>
                <Box>
                  <List data-testid="inbox-sidebar-nav" sx={{ py: 0 }}>
                    {menuItems.map((item, index) => (
                      <ListItemButton
                        key={item.label}
                        selected={tab === index}
                        data-testid={`inbox-nav-${tabMap[index]}`}
                        onClick={() => {
                          setSelectedMessage(null);
                          setTab(index);
                          setActiveTab(tabMap[index]);
                        }}
                        sx={sidebarNavSelectedSx(item.color)}
                      >
                        <ListItemIcon
                          sx={{ minWidth: 32, color: "inherit" }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              fontWeight={tab === index ? "bold" : 500}
                              fontSize="0.86rem"
                              sx={{ color: "inherit" }}
                            >
                              {item.label}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              </Box>
            </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: listBorderColor, display: { xs: "none", md: "block" } }} />
          <Divider sx={{ borderColor: listBorderColor, display: { xs: "block", md: "none" } }} />

          <Box
            sx={{
              width: { xs: "100%", md: 340 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              height: { xs: "auto", md: "calc(80vh - 2px)" },
              maxHeight: "calc(80vh - 2px)",
              minHeight: 200,
            }}
          >
            <Box sx={{ p: 1.25 }}>
              <Box sx={searchBoxSx}>
                <Search sx={{ color: mTokens.textSecondary, fontSize: "1.2rem" }} />
                <InputBase
                  placeholder="Search…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    flex: 1,
                    fontSize: "0.86rem",
                    color: mTokens.textPrimary,
                    "& input::placeholder": { color: mTokens.textSecondary, opacity: 1 },
                  }}
                />
              </Box>
            </Box>
            <Box className="custom-scroll" sx={{ flex: 1, overflowY: "auto" }}>
              {isEmpty ? (
                <Box
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box textAlign="center">
                    <img
                      src={require("../../../assets/emptyEmail.png")}
                      alt="No messages"
                      style={{
                        width: 120,
                        marginBottom: 16,
                        opacity: 0.7,
                      }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: mTokens.textPrimary, fontSize: "0.95rem" }}
                    >
                      {activeTab === "unread"
                        ? "Nothing in unread"
                        : activeTab === "read"
                        ? "Nothing in read"
                        : "Nothing in sent"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: mTokens.textSecondary, fontSize: "0.8rem" }}
                    >
                      {activeTab === "unread"
                        ? "You have no unread messages."
                        : activeTab === "read"
                        ? "You haven't read any messages yet."
                        : "You haven't sent any messages yet."}
                    </Typography>
                  </Box>
                </Box>
              ) : sortedGroups.length === 0 ? (
                <Box
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box textAlign="center">
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: mTokens.textPrimary, fontSize: "0.95rem" }}
                    >
                      No results found
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: mTokens.textSecondary, fontSize: "0.8rem" }}
                    >
                      No messages match "{searchQuery}".
                    </Typography>
                  </Box>
                </Box>
              ) : (
                sortedGroups.map((group) => (
                  <Box key={group} mb={3}>
                    <Typography
                      sx={{
                        px: 2,
                        py: 1,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: mTokens.textSecondary,
                      }}
                    >
                      {group}
                    </Typography>
                    <Box borderRadius={1}>
                      {grouped[group].map((item, index) => (
                        <Box
                          key={index}
                          onClick={() => setSelectedMessage(item)}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            px: 2,
                            py: 1.5,
                            m: 1,
                            borderRadius: "12px",
                            cursor: "pointer",
                            border: `1px solid transparent`,
                            transition: "background-color 0.15s ease, border-color 0.15s ease",
                            "&:hover": {
                              backgroundColor: listHoverBg,
                              borderColor: listBorderColor,
                            },
                            bgcolor:
                              selectedMessage?.notification
                                .notificationId ===
                              item.notification.notificationId
                                ? listSelectedBg
                                : "inherit",
                            ...(selectedMessage?.notification
                              .notificationId ===
                            item.notification.notificationId
                              ? { borderColor: "rgba(20,184,166,0.35)" }
                              : {}),
                          }}
                        >
                          <Box mr={2}>
                            <Box mr={1}>
                              {(() => {
                                const { doc, fallback } =
                                  getListItemAvatarDoc(item);
                                const src = buildAvatarSrc(doc);
                                return (
                                  <Avatar
                                    src={src}
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      fontWeight: "bold",
                                      fontSize: "0.9rem",
                                      bgcolor: src
                                        ? "transparent"
                                        : "#64748B",
                                    }}
                                  >
                                    {!src && fallback}
                                  </Avatar>
                                );
                              })()}
                            </Box>
                          </Box>
                          <Box
                            width="100%"
                            sx={{ minWidth: 0 }}
                          >
                            <Box
                              display="flex"
                              alignItems="baseline"
                              justifyContent="space-between"
                              gap={1}
                            >
                              <Typography
                                fontWeight="bold"
                                fontSize="0.86rem"
                                noWrap
                                sx={{ color: mTokens.textPrimary, minWidth: 0 }}
                              >
                                {chooseTitleName(item)}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.25} flexShrink={0}>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{ color: mTokens.textSecondary, fontSize: "0.72rem" }}
                                >
                                  {item.formattedTime}
                                </Typography>
                                {activeTab === "unread" && (
                                  <CustomTooltip title="Mark as Read" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedMessage(item);
                                        handleAck(item.notification);
                                      }}
                                      sx={{ ...tintedIconBtnSx(navColors.unread), ml: 0.25, p: 0.4 }}
                                    >
                                      <MarkChatRead sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </CustomTooltip>
                                )}
                              </Box>
                            </Box>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                color: mTokens.textSecondary,
                                fontSize: "0.8rem",
                                mt: 0.15,
                              }}
                            >
                              {item.notification.parentNotification !=
                              null
                                ? item.notification.parentNotification
                                    .notificationSub
                                : item.notification.notificationSub ||
                                  "No subject"}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: listBorderColor, display: { xs: "none", md: "block" } }} />
          <Divider sx={{ borderColor: listBorderColor, display: { xs: "block", md: "none" } }} />

          <Box
            className="custom-scroll"
            sx={{
              flex: 1,
              minWidth: 0,
              height: "calc(80vh - 2px)",
              overflowY: "auto",
            }}
          >
              {selectedMessage ? (
                <>
                  {threadMessages.length > 0 &&
                    threadMessages
                      .filter((msg) => {
                        const parent =
                          selectedMessage?.notification?.parentNotification;
                        if (
                          selectedMessage?.isMulti &&
                          parent &&
                          parent != null &&
                          parent.notificationType !== "TO_USER" &&
                          parent.senderId === userInfo.userId
                        ) {
                          return true;
                        }
                        return isBelongtoThisUser(msg);
                      })
                      .map((msg) => (
                        <Box key={msg.notificationId} sx={{ mb: 2 }}>
                          {msg.notificationId ===
                            threadMessages[0].notificationId && (
                            <>
                              <Box
                                p={2}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ borderBottom: `1px dashed ${listBorderColor}` }}
                              >
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  sx={{ color: mTokens.textPrimary, fontSize: "0.98rem" }}
                                >
                                  {threadMessages.length > 1
                                    ? threadMessages[
                                        threadMessages.length - 1
                                      ].notificationSub
                                    : msg.notificationSub || "No subject"}
                                </Typography>
                              </Box>
                            </>
                          )}
 
                          {selectedMessage &&
                            replyForId &&
                            msg.notificationId === replyForId && (
                              <Box
                                sx={{
                                  mt: { md: 2, xs: 0 },
                                  mx: { md: 3, xs: 0 },
                                  border: `1px solid ${listBorderColor}`,
                                  borderRadius: "14px",
                                  backgroundColor: isDark
                                    ? mTokens.cardBg
                                    : mTokens.bg,
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={2}
                                  p={2}
                                >
                                  {(() => {
                                    const { doc, fallback } =
                                      getReplyHeaderAvatarDoc(
                                        selectedMessage,
                                        msg,
                                        activeTab
                                      );
                                    const src = buildAvatarSrc(doc);
                                    return (
                                      <Avatar
                                        src={src}
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          fontWeight: "bold",
                                          bgcolor: src
                                            ? "transparent"
                                            : "#64748B",
                                        }}
                                      >
                                        {!src && fallback}
                                      </Avatar>
                                    );
                                  })()}
                                  <Box>
                                    <Typography sx={{ color: mTokens.textPrimary, fontSize: "0.86rem" }}>
                                      {activeTab === "sent"
                                        ? selectedMessage.notification
                                            .targetName
                                        : selectedMessage.notification
                                            .notificationAsgnTo.length > 0
                                        ? selectedMessage.notification
                                            .notificationAsgnTo[0]
                                            .assignedToAuditUsername
                                        : msg.senderName}
                                    </Typography>
                                  </Box>
                                </Box>
 
                                <ReplyBox
                                  placeholder="Type your reply..."
                                  userOptions={replyBoxUserOptions}
                                  toUserId={
                                    selectedMessage.notification
                                      .notificationAsgnTo.length > 0
                                      ? selectedMessage.notification
                                          .notificationAsgnTo[0]
                                          .assignedToAuditUserId
                                      : msg.senderId
                                  }
                                  onSend={async (html, atts, cc, bcc) => {
                                    sendingReplyBoxFunction(
                                      "",
                                      selectedMessage.notification
                                        .notificationAsgnTo.length > 0
                                        ? [
                                            selectedMessage.notification
                                              .notificationAsgnTo[0]
                                              .assignedToAuditUserId,
                                          ]
                                        : [msg.senderId],
                                      `Reply: ${
                                        selectedMessage.notification
                                          .parentNotification
                                          ? selectedMessage.notification
                                              .parentNotification
                                              .notificationSub
                                          : selectedMessage.notification
                                              .notificationSub
                                      }`,
                                      html,
                                      selectedMessage.notification
                                        .important === 1
                                        ? true
                                        : false,
                                      atts,
                                      selectedMessage.notification
                                        .notificationAsgnTo.length > 0
                                        ? selectedMessage.notification
                                            .notificationId
                                        : selectedMessage.notification
                                            .parentNotification != null
                                        ? selectedMessage.notification
                                            .parentNotification
                                            .notificationId
                                        : selectedMessage.notification
                                            .notificationId,
                                      msg,
                                      cc,
                                      bcc
                                    );
                                  }}
                                  onSent={() => {
                                    dispatch(setSingleAlertObj({ message: "Message sent.", alertType: "success" }));
                                  }}
                                  onAttachFiles={saveReplyBoxNotificationDocs}
                                  onCancel={closeReply}
                                  ccIds={replyMode === 'replyAll' ? returnCCIds(msg) : []}
                                  // bccIds={replyMode === 'replyAll' ? msg?.bccIds || msg?.notification?.bccIds : []}
                                  onRemoveAttachment={handleReplyBoxRemoveAttachment}
                                />
                              </Box>
                            )}

                          <Box
                            sx={{
                              pl: { md: 3, xs: 0 },
                              pr: { md: 3, xs: 0 },
                              pt: { md: 2, xs: 0 },
                            }}
                          >
                            <Box
                              gap={2}
                              px={2}
                              sx={{
                                ...(msg.notificationId ===
                                threadMessages[0].notificationId
                                  ? {}
                                  : {
                                      borderTop: `1px dashed ${listBorderColor}`,
                                      pt: 2,
                                    }),
                              }}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={2}
                                p={2}
                                sx={{ cursor: "pointer" }}
                                onClick={() =>
                                  toggleCollapsed(msg.notificationId)
                                }
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Enter" ||
                                    e.key === " "
                                  ) {
                                    e.preventDefault();
                                    toggleCollapsed(msg.notificationId);
                                  }
                                }}
                              >
                                {(() => {
                                  const multi = threadMessages.length > 1;
                                  const { doc, fallback } =
                                    getThreadMessageAvatarDoc(
                                      selectedMessage,
                                      msg,
                                      multi,
                                      activeTab
                                    );
                                  const src = buildAvatarSrc(doc);
                                  return (
                                    <Avatar
                                      src={src}
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        fontWeight: "bold",
                                        bgcolor: src
                                          ? "transparent"
                                          : "#64748B",
                                      }}
                                    >
                                      {!src && fallback}
                                    </Avatar>
                                  );
                                })()}
                                <Box>
                                  <Typography sx={{ color: mTokens.textPrimary, fontSize: "0.86rem" }}>
                                    {threadMessages.length > 1
                                      ? msg.senderName
                                      : activeTab === "sent"
                                      ? msg.targetName
                                      : msg.senderName}
                                    {msg.notificationAsgnTo.length > 0
                                      ? `  {Assigned to ${msg.notificationAsgnTo[0].assignedToUsername} by 
                                        ${msg.notificationAsgnTo[0].assignedToAuditUsername} }`
                                      : ""}
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: mTokens.textSecondary }}
                                    >
                                      {msg.formattedDate} {msg.formattedTime}
                                    </Typography>
                                    <MessageMetaChip
                                      msg={msg}
                                      activeTab={activeTab}
                                      userId={userInfo.userId}
                                    />
                                  </Box>
                                </Box>
                              </Box>

                              <Collapse
                                in={!isCollapsed(msg.notificationId)}
                                timeout="auto"
                                unmountOnExit
                              >
                                {/* CS-1580 - Start */}
                                <Box
                                  px={2}
                                  sx={{
                                    // Message bodies are arbitrary HTML, so anchor the
                                    // base size to the app scale instead of the 16px default.
                                    fontSize: "0.86rem",
                                    lineHeight: 1.6,
                                    "& p, & span, & div, & li, & td": {
                                      fontSize: "0.86rem",
                                    },
                                    "& a": {
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "#82b1ff"
                                          : "#1976d2",
                                      textDecoration: "underline",
                                      cursor: "pointer",
                                    },
                                    "& a:hover": {
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "#bbdefb"
                                          : "#1565c0",
                                    },
                                    "& a:visited": {
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "#b388ff"
                                          : "#6a1b9a",
                                    },
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: msg.notificationMsg,
                                  }}
                                />
                                {/* CS-1580 - End */}

                                {msg.notificationAttachment.length > 0 && (
                                  <Box
                                    mt={2}
                                    sx={{
                                      bgcolor: isDark
                                        ? "rgba(20,184,166,0.1)"
                                        : "rgba(20,184,166,0.08)",
                                      border: `1px solid ${
                                        isDark
                                          ? "rgba(20,184,166,0.22)"
                                          : "rgba(20,184,166,0.2)"
                                      }`,
                                      borderRadius: "12px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      px={2}
                                      py={1}
                                      borderRadius={2}
                                      onClick={() =>
                                        setShowAttachments(!showAttachments)
                                      }
                                      sx={{ cursor: "pointer" }}
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <AttachFile
                                          fontSize="small"
                                          sx={{ color: actionColors.attachment }}
                                        />
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{ color: mTokens.textPrimary, fontSize: "0.8rem" }}
                                        >
                                          {
                                            msg.notificationAttachment
                                              .length
                                          }{" "}
                                          attachment
                                          {msg.notificationAttachment
                                            .length > 1
                                            ? "s"
                                            : ""}
                                        </Typography>
                                        {showAttachments ? (
                                          <ExpandLess sx={{ color: mTokens.textSecondary, fontSize: "1.1rem" }} />
                                        ) : (
                                          <ExpandMore sx={{ color: mTokens.textSecondary, fontSize: "1.1rem" }} />
                                        )}
                                      </Box>

                                      <Box sx={{ ...pillGroupSx, p: 0.3, gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadAll(
                                              msg.notificationAttachment.map(
                                                (attmt: {
                                                  documentId: number;
                                                }) => attmt.documentId
                                              )
                                            );
                                          }}
                                          sx={tintedIconBtnSx(actionColors.download)}
                                        >
                                          <Download fontSize="small" />
                                        </IconButton>
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          sx={{ fontSize: "0.8rem", color: actionColors.download }}
                                        >
                                          Download
                                        </Typography>
                                      </Box>
                                    </Box>

                                    <Collapse in={showAttachments}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          overflowX: "auto",
                                          gap: 1,
                                          px: 1,
                                          py: 1,
                                          flexWrap: isMobile
                                            ? "nowrap"
                                            : "wrap",
                                        }}
                                      >
                                        {msg.notificationAttachment.map(
                                          (
                                            attachment: any,
                                            index: number
                                          ) => (
                                            <Box
                                              key={index}
                                              display="flex"
                                              justifyContent="space-between"
                                              alignItems="center"
                                              px={2}
                                              py={1}
                                              bgcolor={
                                                isDark
                                                  ? "rgba(255,255,255,0.04)"
                                                  : "rgba(15,23,42,0.04)"
                                              }
                                              minWidth={
                                                isMobile ? 220 : "auto"
                                              }
                                              sx={{
                                                position: "relative",
                                                borderRadius: "10px",
                                                border: `1px solid ${mTokens.border}`,
                                                transition: "border-color 0.18s ease",
                                                "&:hover": {
                                                  borderColor: `${actionColors.attachment}55`,
                                                },
                                                "&:hover .hover-icon": {
                                                  opacity: 1,
                                                },
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                noWrap
                                                sx={{ flexGrow: 1, mr: 1, fontSize: "0.8rem" }}
                                              >
                                                {attachment.documentName}
                                              </Typography>

                                              <Box sx={{ ...pillGroupSx, p: 0.25, gap: 0.15 }}>
                                                <IconButton
                                                  size="small"
                                                  className="hover-icon"
                                                  onClick={() =>
                                                    handleActionPreview(
                                                      attachment,
                                                      "document"
                                                    )
                                                  }
                                                  sx={{
                                                    ...tintedIconBtnSx(actionColors.preview),
                                                    opacity: 0,
                                                    transition:
                                                      "opacity 0.3s ease, color 0.18s ease, background-color 0.18s ease, transform 0.18s ease",
                                                  }}
                                                >
                                                  <Visibility fontSize="small" />
                                                </IconButton>

                                                <IconButton
                                                  size="small"
                                                  className="hover-icon"
                                                  onClick={() =>
                                                    handleDownloadAll([
                                                      attachment.documentId,
                                                    ])
                                                  }
                                                  sx={{
                                                    ...tintedIconBtnSx(actionColors.download),
                                                    opacity: 0,
                                                    transition:
                                                      "opacity 0.3s ease, color 0.18s ease, background-color 0.18s ease, transform 0.18s ease",
                                                  }}
                                                >
                                                  <Download fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            </Box>
                                          )
                                        )}
                                      </Box>
                                    </Collapse>
                                  </Box>
                                )}

                                <Box
                                  justifyContent="end"
                                  display="flex"
                                  mt={1}
                                  gap={2}
                                  mb={2}
                                >
                                  {msg.isAssignable && (
                                    // CS-1397 - Start
                                    <Button
                                      size="large"
                                      variant="outlined"
                                      color="success"
                                      startIcon={<AssignmentInd />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAssign();
                                      }}
                                      removeUpperCase
                                      sx={exportBtnSx(actionColors.assign)}
                                    >
                                      Assign
                                    </Button>
                                  )}
                                  {activeTab != "sent" &&
                                    msg.senderId != userInfo.userId && (
                                      <Button
                                        size="large"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<Reply />}
                                        removeUpperCase
                                        sx={exportBtnSx(actionColors.reply)}
                                        onClick={() =>
                                          msg && openReplyFor(msg, "reply")
                                        }
                                      >
                                        Reply
                                      </Button>
                                      // CS-1397 - End
                                    )}
                                  {activeTab != "sent" && (ccAndBccValidation(msg))
                                  && msg.senderId != userInfo.userId && (
                                      <Button
                                        size="large"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<ReplyAll />}
                                        removeUpperCase
                                        sx={exportBtnSx(actionColors.replyAll)}
                                        onClick={() =>
                                          msg && openReplyFor(msg, "replyAll")
                                        }
                                      >
                                        Reply All
                                      </Button>
                                      // CS-1397 - End
                                    )}
                                </Box>
                              </Collapse>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                </>
              ) : (
                !isEmpty && (
                  <Box
                    height="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box textAlign="center">
                      <img
                        src={require("../../../assets/email.png")}
                        alt="No message selected"
                        style={{
                          width: 120,
                          marginBottom: 16,
                          opacity: 0.7,
                        }}
                      />
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: mTokens.textPrimary, fontSize: "0.95rem" }}
                      >
                        Select an item to read
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: mTokens.textSecondary, fontSize: "0.8rem" }}
                      >
                        Nothing is selected
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </Box>
      </Box>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        disablePortal
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              mt: 1,
              p: 2,
              border: `1px solid ${mTokens.border}`,
              boxShadow: isDark
                ? "0 12px 32px rgba(0,0,0,0.28)"
                : "0 12px 28px rgba(15,23,42,0.08)",
              borderRadius: "14px",
              backgroundColor: mTokens.bg,
              minWidth: 240,
              zIndex: 9999,
            }}
          >
            <Typography
              fontWeight="bold"
              fontSize="0.86rem"
              mb={1}
              textAlign="center"
              sx={{ color: mTokens.textPrimary }}
            >
              Choose Recipient Type
            </Typography>
            <Box display="flex" gap={2}>
              {accessibleTypes.includes("GROUP") && (
                <Button
                  variant="contained"
                  fullWidth
                  color="info"
                  removeUpperCase
                  sx={primaryActionBtnSx}
                  onClick={() => {
                    setComposeMode("group");
                    setShowPopup(true);
                    setAnchorEl(null);
                  }}
                >
                  Group
                </Button>
              )}
              {accessibleTypes.includes("USER") && (
                <Button
                  variant="contained"
                  fullWidth
                  color="info"
                  removeUpperCase
                  sx={primaryActionBtnSx}
                  onClick={() => {
                    setComposeMode("user");
                    setShowPopup(true);
                    setAnchorEl(null);
                  }}
                >
                  User
                </Button>
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper> 

      {showPopup && composeMode && (
        <Box
          sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1300 }}
        >
          <ComposePopup
            mode={composeMode}
            parentNotification={
              composeMode === "reply"
                ? selectedMessage?.notification
                : null
            }
            onSend={(
              toValue,
              entity,
              subject,
              message,
              important,
              attachments,
              parentId,
              cc,
              bcc
            ) => {
              sendingComposePopupFunction(
                toValue,
                entity,
                subject,
                message,
                important,
                attachments,
                parentId,
                undefined,
                cc,
                bcc
              );
            }}
            onClose={closeComposePopup}
            onRemoveAttachments={removeComposePopupAttachments}
            onAttachFiles={saveComposePopupNotificationDocs}
            onRemoveAttachment={handleComposePopupRemoveAttachment}
          />
        </Box>
      )}

      {showPDF.isShow && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => {
            setShowPDF({ isShow: false, title: "", fileName: "" });
          }}
          title={showPDF.title}
          fileName={showPDF.fileName}
          base64String={
            documentDataFromCommonApi?.content
              ? documentDataFromCommonApi?.content
              : pdfContent
          }
        />
      )}

      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() =>
            setShowOptionsPopup({ message: "", show: false })
          }
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}
    </Box>
  );
};

export default InboxList;